import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Dropdown from "../../components/Dropdown";
import IconCaretDown from "../../components/Icon/IconCaretDown";
import eventService from "../../service/event.service";
import { DataTable } from "mantine-datatable";
import IconUsers from "../../components/Icon/IconUsers";
import IconTrophy from "../../components/Icon/IconTrophy";
import IconSwimming from "../../components/Icon/IconSwimming";
import IconBike from "../../components/Icon/IconBike";
import IconRun from "../../components/Icon/IconRun";

const Dashboard = () => {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        totalUsers: 0,
        teamCreations: 0,
        topPlayers: [],
        topRacePlayers: [],
        topSwimPlayers: [],
        topBikePlayers: []
    });

    // Fetch events for dropdown
    const fetchEvents = async () => {
        try {
            const response = await eventService.getAllEvents();
            const eventsData = response?.data?.data || [];
            setEvents(eventsData);
            if (eventsData.length > 0) {
                setSelectedEvent(eventsData[0]);
            }
        } catch (error) {
        }
    };


    // Fetch real dashboard data from API
    const fetchDashboardData = useCallback(async (eventIds) => {

        setLoading(true);

        try {
            const response = await eventService.getDashboardData({event_id: eventIds});
            const apiData = response?.data?.data;

            if (apiData) {
                const formattedData = {
                    totalUsers: apiData.userCount || 0,
                    teamCreations: apiData.totalTeamsCount || 0,
                    topPlayers: apiData.topSelectedAthletes || [],
                    topRacePlayers: apiData.topBoostedAthletes?.runner || [],
                    topSwimPlayers: apiData.topBoostedAthletes?.swimmer || [],
                    topBikePlayers: apiData.topBoostedAthletes?.biker || []
                };

                setDashboardData(formattedData);
            }
        } catch (error) {
            // Fallback to empty data
            setDashboardData({
                totalUsers: 0,
                teamCreations: 0,
                topPlayers: [],
                topRacePlayers: [],
                topSwimPlayers: [],
                topBikePlayers: []
            });
        } finally {
            setLoading(false);
        }
    }, []);




    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        console.log( "selectedEvent ", selectedEvent);
        if (selectedEvent) {
            fetchDashboardData(selectedEvent.event_ids);
        }
    }, [selectedEvent, fetchDashboardData]);

    const eventOptions = events.map((event) => ({
        value: event.event_id,
        label: `${event.event_title}`,
        event: event
    }));

    const handleEventChange = (option) => {
        setSelectedEvent(option.event);
        console.log( "option event ", option.event);
    };

    const StatCard = ({ title, value, icon: Icon, color = "primary" }) => (
        <div className="panel">
            <div className="flex items-center justify-between">
                <div>
                    <h6 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h6>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
                </div>
                <div className={`p-3 rounded-full bg-${color}-100 dark:bg-${color}-900/20`}>
                    <Icon className={`h-6 w-6 text-${color}-600 dark:text-${color}-400`} />
                </div>
            </div>
        </div>
    );

    const TopPlayersTable = ({ title, data, columns, icon: Icon, color = "primary" }) => (
        <div className="panel">
            <div className="flex items-center gap-2 mb-4">
                <Icon className={`h-5 w-5 text-${color}-600`} />
                <h5 className="font-semibold text-lg dark:text-white-light">{title}</h5>
            </div>
            <DataTable
                records={data}
                columns={columns}
                loading={loading}
                noRecordsText="No data available"
                className="whitespace-nowrap"
                withBorder={false}
                withColumnBorders={false}
                scrollAreaProps={{
                    scrollbarSize: 0,
                    scrollHideDelay: 0
                }}
                styles={{
                    scrollArea: {
                        overflow: 'hidden'
                    },
                    table: {
                        height: 'auto'
                    }
                }}
            />
        </div>
    );

    const playerColumns = [
        {
            accessor: "athlete_id",
            title: "No.",
            render: (record, index) => <span className="font-semibold text-lg">#{index + 1}</span>
        },
        {
            accessor: "athlete_first",
            title: "Name",
            render: ({ athlete_first, athlete_last }) => (
                <div className="font-medium text-gray-900 dark:text-white">
                    {athlete_first} {athlete_last}
                </div>
            )
        },
        {
            accessor: "selectionCount",
            title: "No. of Teams",
            render: ({ selectionCount, selectionPercentage }) => (
                <div className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                    {selectionCount || 0} ({selectionPercentage || 0}%)
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedEvent ? `Viewing data for ${selectedEvent.event_title}` : "Select an event to view statistics"}
                    </p>
                </div>
                <div className="dropdown">
                    <Dropdown
                        btnClassName="btn btn-outline-primary dropdown-toggle shadow-none"
                        button={
                            <>
                                {selectedEvent ? `${selectedEvent.event_title} ` : "Select Event"}
                                <span>
                                    <IconCaretDown className="ltr:ml-1 rtl:mr-1 inline-block" />
                                </span>
                            </>
                        }
                        options={eventOptions}
                        onOptionSelect={handleEventChange}
                    />
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Total Users"
                    value={dashboardData.totalUsers.toLocaleString()}
                    icon={IconUsers}
                    color="primary"
                />
                <StatCard
                    title="Teams Created"
                    value={dashboardData.teamCreations.toLocaleString()}
                    icon={IconTrophy}
                    color="success"
                />
            </div>

            {/* Top Players Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                <TopPlayersTable
                    title="Top 10 Players"
                    data={dashboardData.topPlayers}
                    columns={playerColumns}
                    icon={IconTrophy}
                    color="warning"
                />
                <TopPlayersTable
                    title="Top 10 Race Players"
                    data={dashboardData.topRacePlayers}
                    columns={playerColumns}
                    icon={IconRun}
                    color="primary"
                />
                <TopPlayersTable
                    title="Top 10 Swim Players"
                    data={dashboardData.topSwimPlayers}
                    columns={playerColumns}
                    icon={IconSwimming}
                    color="info"
                />
                <TopPlayersTable
                    title="Top 10 Bike Players"
                    data={dashboardData.topBikePlayers}
                    columns={playerColumns}
                    icon={IconBike}
                    color="success"
                />
            </div>
        </div>
    );
};

export default Dashboard;
